// The draft route (DRAFT_ROOM.md, DRAFT_MODES.md, BACKLOG D10).
//
// THREE ROOMS, ONE SCREEN. The practice league's draft runs on the
// simulator; a real league that drafts in the room runs over the room's
// socket; a real league that drafts by itself or is typed in afterward is read
// as a lobby and then a board (`LeagueDraftScreen`). The first two draw the
// same `DraftRoom`, handed a view and a way to send intents.

import { useEffect, useState } from 'react';
import type { DraftClientMessage } from '@fsp/domain';
import type { DraftIntent } from '@fsp/draft';
import { useLeagueBase, useDraft, useLeagueDraft } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { intentId, useLiveDraft, type LiveDraft } from '../data/liveDraft';
import { useSim } from '../data/SimProvider';
import { tourDone } from '../demo/chapters';
import { EmptyState } from '../kit';
import { tell } from '../kit/ui';
import { FromLeague } from './shared/FromLeague';
import { LeagueDraftBody } from './LeagueDraftScreen';
import { DraftRoom, type RoomActions } from './draft/DraftRoom';

export function DraftRoomScreen() {
  const source = useLeagueSource();
  if (source === 'api') return <RealLeagueDraft />;
  return <PracticeDraftRoom />;
}

/** A real league: its room while it drafts there, and the stored board otherwise. */
function RealLeagueDraft() {
  return (
    <FromLeague snapshot={useLeagueDraft()}>
      {(view) =>
        view.mode === 'live' && view.status !== 'complete' ? (
          <LiveDraftRoom leagueId={view.league.id} />
        ) : (
          <LeagueDraftBody view={view} />
        )
      }
    </FromLeague>
  );
}

/** This device's clock: the room's countdown is drawn against it and the measured offset. */
export const deviceNow = () => Date.now();

/** The room's clock, a few times a second, so a countdown moves. */
export function useTicking(ms = 250): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** The room's actions as intents over its socket: the league's room, or a league mock's. */
export function socketActions(over: LiveDraft['send']): RoomActions {
  const send = (message: DraftClientMessage) => {
    if (!over(message)) tell('The room is not connected yet. Try again in a moment.', 'bad');
  };
  return {
    pick: (playerId, forTeam) =>
      send({
        type: 'draft.pick',
        payload: { intentId: intentId(), playerId, ...(forTeam ? { teamId: forTeam } : {}) },
      }),
    setQueue: (playerIds) =>
      send({ type: 'draft.queue.set', payload: { intentId: intentId(), playerIds } }),
    start: () => send({ type: 'draft.start', payload: { intentId: intentId() } }),
    pause: () => send({ type: 'draft.pause', payload: { intentId: intentId() } }),
    resume: () => send({ type: 'draft.resume', payload: { intentId: intentId() } }),
    undo: () => send({ type: 'draft.undo', payload: { intentId: intentId() } }),
    setAutopilot: (on, teamId) =>
      send({
        type: 'draft.autopilot.set',
        payload: { intentId: intentId(), on, ...(teamId ? { teamId } : {}) },
      }),
    takeSeat: (teamId) =>
      send({ type: 'draft.seat.take', payload: { intentId: intentId(), teamId } }),
    leaveSeat: () => send({ type: 'draft.seat.leave', payload: { intentId: intentId() } }),
    trade: {
      offer: (toTeamId, give, get, replaces) =>
        send({
          type: 'draft.trade.offer',
          payload: {
            intentId: intentId(),
            toTeamId,
            give,
            get,
            ...(replaces === null ? {} : { replaces }),
          },
        }),
      answer: (offerId, accept) =>
        send({ type: 'draft.trade.answer', payload: { intentId: intentId(), offerId, accept } }),
      withdraw: (offerId) =>
        send({ type: 'draft.trade.withdraw', payload: { intentId: intentId(), offerId } }),
    },
    undoTrade: () => send({ type: 'draft.trade.undo', payload: { intentId: intentId() } }),
    // AN AUCTION'S (AUCTION.md §3).
    auction: {
      nominate: (playerId, bid, forTeam) =>
        send({
          type: 'draft.nominate',
          payload: { intentId: intentId(), playerId, bid, ...(forTeam ? { teamId: forTeam } : {}) },
        }),
      bid: (lotId, amount) =>
        send({ type: 'draft.bid', payload: { intentId: intentId(), lotId, amount } }),
      autoBid: (lotId, max) =>
        send({ type: 'draft.bid.auto', payload: { intentId: intentId(), lotId, max } }),
    },
  };
}

/** A real league's room, over its socket (REALTIME.md). */
function LiveDraftRoom({ leagueId }: { leagueId: string }) {
  const base = useLeagueBase();
  const live = useLiveDraft(leagueId, (refusal) => tell(refusal.message, 'bad'), deviceNow);
  const now = useTicking() + live.offset;
  if (live.view === null) {
    return (
      <EmptyState>
        {live.connection === 'signed-out'
          ? 'Your session ended. Sign in again to come back to the room.'
          : live.connection === 'offline'
            ? 'The draft room cannot be reached right now. This page keeps trying.'
            : 'Joining the draft room…'}
      </EmptyState>
    );
  }
  const actions = socketActions(live.send);
  return (
    <DraftRoom
      view={live.view}
      now={now}
      connection={live.connection}
      actions={actions}
      recapTo={`${base}/draft-recap`}
      teamTo={base}
      offerNews={live.closedOffers}
      tvTo={`${base}/draft/tv`}
    />
  );
}

/** The practice league's room, on the simulator. */
function PracticeDraftRoom() {
  const view = useDraft();
  const sim = useSim();
  const base = useLeagueBase();
  const mine = view.myTeamId;
  const run = (intent: DraftIntent) => {
    const events = sim.draftIntent(intent);
    const refused = events.find((e) => e.type === 'rejected');
    if (refused && refused.type === 'rejected') tell(refused.message, 'bad');
    return refused === undefined;
  };
  const actions: RoomActions = {
    pick: (playerId, forTeam) => {
      const teamId = forTeam ?? mine;
      if (teamId === null) return;
      if (run({ type: 'pick', teamId, playerId }) && forTeam === null) tourDone('pick');
    },
    setQueue: (playerIds) => {
      if (mine !== null) run({ type: 'queue.set', teamId: mine, playerIds });
    },
    start: () => {
      sim.draftStart();
    },
    pause: () => run({ type: 'pause', by: 'Commissioner' }),
    resume: () => run({ type: 'resume', by: 'Commissioner' }),
    undo: () => run({ type: 'undo', by: 'Commissioner' }),
    setAutopilot: (on, teamId) => {
      const team = teamId ?? mine;
      if (team !== null) run({ type: 'autopilot.set', teamId: team, on });
    },
  };
  return (
    <DraftRoom
      view={view}
      now={sim.now}
      connection="practice"
      actions={actions}
      recapTo={`${base}/draft-recap`}
      teamTo={base}
      tvTo={`${base}/draft/tv`}
    />
  );
}
